import { useState, useCallback, useMemo } from "react";
import {
  Shield,
  Plus,
  Play,
  CheckCircle,
  XCircle,
  Download,
  Trash2,
  FileEdit,
  Eye,
  Clock,
  AlertTriangle,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import {
  useGDPRRequests,
  useCreateGDPRRequest,
  useProcessGDPRRequest,
  useCompleteGDPRRequest,
  useRejectGDPRRequest,
} from "@/hooks/admin/useEnterprise";
import type {
  GDPRRequest,
  GDPRRequestType,
  GDPRRequestStatus,
  CreateGDPRRequestInput,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<GDPRRequestType, string> = {
  export: "Export",
  delete: "Suppression",
  rectification: "Rectification",
  access: "Acces",
};

const TYPE_ICONS: Record<GDPRRequestType, typeof Download> = {
  export: Download,
  delete: Trash2,
  rectification: FileEdit,
  access: Eye,
};

const TYPE_COLORS: Record<GDPRRequestType, string> = {
  export: "bg-blue-100 text-blue-800 border-blue-200",
  delete: "bg-red-100 text-red-800 border-red-200",
  rectification: "bg-amber-100 text-amber-800 border-amber-200",
  access: "bg-stone-100 text-stone-700 border-stone-200",
};

const STATUS_LABELS: Record<GDPRRequestStatus, string> = {
  pending: "Recue",
  processing: "En cours",
  completed: "Terminee",
  rejected: "Rejetee",
};

const STATUS_COLORS: Record<GDPRRequestStatus, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const SLA_DAYS = 30;

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "type",
    label: "Type",
    type: "select",
    options: [
      { value: "export", label: "Export" },
      { value: "delete", label: "Suppression" },
      { value: "rectification", label: "Rectification" },
      { value: "access", label: "Acces" },
    ],
  },
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: [
      { value: "pending", label: "Recue" },
      { value: "processing", label: "En cours" },
      { value: "completed", label: "Terminee" },
      { value: "rejected", label: "Rejetee" },
    ],
  },
  { key: "date_from", label: "Du", type: "date" },
  { key: "date_to", label: "Au", type: "date" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getDaysRemaining(submittedAt: string): number {
  const submitted = new Date(submittedAt);
  const deadline = new Date(submitted);
  deadline.setDate(deadline.getDate() + SLA_DAYS);
  return Math.ceil((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function isOverdue(request: GDPRRequest): boolean {
  if (request.status === "completed" || request.status === "rejected") return false;
  return getDaysRemaining(request.submitted_at) < 0;
}

// ---------------------------------------------------------------------------
// SLA Countdown Badge
// ---------------------------------------------------------------------------

function SLABadge({ request }: { readonly request: GDPRRequest }) {
  if (request.status === "completed" || request.status === "rejected") {
    return null;
  }

  const daysLeft = getDaysRemaining(request.submitted_at);
  const overdue = daysLeft < 0;
  const urgent = daysLeft >= 0 && daysLeft <= 5;

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
        overdue
          ? "border-red-200 bg-red-100 text-red-700"
          : urgent
          ? "border-amber-200 bg-amber-100 text-amber-700"
          : "border-stone-200 bg-stone-50 text-stone-600"
      }`}
    >
      <Clock className="h-3 w-3" />
      {overdue ? `${Math.abs(daysLeft)}j en retard` : `${daysLeft}j restants`}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create GDPR Request Modal
// ---------------------------------------------------------------------------

interface CreateGDPRModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateGDPRRequestInput) => Promise<void>;
}

function CreateGDPRModal({ open, onClose, isSaving, onSave }: CreateGDPRModalProps) {
  const [type, setType] = useState<GDPRRequestType>("access");
  const [subjectEmail, setSubjectEmail] = useState("");
  const [subjectName, setSubjectName] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setType("access");
    setSubjectEmail("");
    setSubjectName("");
    setNotes("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!subjectEmail.trim()) {
      setError("L'adresse e-mail est requise.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subjectEmail.trim())) {
      setError("L'adresse e-mail n'est pas valide.");
      return;
    }
    if (!subjectName.trim()) {
      setError("Le nom du sujet est requis.");
      return;
    }
    setError(null);
    try {
      await onSave({
        type,
        subject_email: subjectEmail.trim(),
        subject_name: subjectName.trim(),
        notes: notes.trim() || undefined,
      });
      toast.success("Demande RGPD creee avec succes");
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue.";
      setError(message);
      toast.error("Echec de la creation de la demande");
    }
  }, [type, subjectEmail, subjectName, notes, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvelle demande RGPD"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Creation..." : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Type selector */}
        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">
            Type de demande <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(Object.entries(TYPE_LABELS) as [GDPRRequestType, string][]).map(
              ([value, label]) => {
                const Icon = TYPE_ICONS[value];
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setType(value)}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                      type === value
                        ? TYPE_COLORS[value]
                        : "border-stone-200 text-stone-600 hover:bg-stone-50"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                );
              },
            )}
          </div>
        </div>

        {/* Subject info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              E-mail du sujet <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              placeholder="email@exemple.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Nom du sujet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              placeholder="Prenom Nom"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Contexte ou informations complementaires..."
          />
        </div>

        {/* SLA reminder */}
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <p className="text-xs text-blue-700">
            Delai legal de traitement : <span className="font-semibold">30 jours</span> a compter
            de la date de soumission.
          </p>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function GDPR() {
  useAdminPageTitle("Conformite RGPD");

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showCreate, setShowCreate] = useState(false);

  const params = {
    page,
    limit: 20,
    type: (filters.type as GDPRRequestType) ?? undefined,
    status: (filters.status as GDPRRequestStatus) ?? undefined,
    date_from: (filters.date_from as string) ?? undefined,
    date_to: (filters.date_to as string) ?? undefined,
  };

  const { data, isLoading, isError } = useGDPRRequests(params);
  const createMutation = useCreateGDPRRequest();
  const processMutation = useProcessGDPRRequest();
  const completeMutation = useCompleteGDPRRequest();
  const rejectMutation = useRejectGDPRRequest();

  const requests = data?.requests ?? [];
  const pagination = data?.pagination;

  // KPI calculations
  const totalRequests = useMemo(() => requests.length, [requests]);
  const processingCount = useMemo(
    () => requests.filter((r) => r.status === "processing" || r.status === "pending").length,
    [requests],
  );
  const completedCount = useMemo(
    () => requests.filter((r) => r.status === "completed").length,
    [requests],
  );
  const overdueCount = useMemo(
    () => requests.filter((r) => isOverdue(r)).length,
    [requests],
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateGDPRRequestInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const handleProcess = useCallback(
    async (id: string) => {
      try {
        await processMutation.mutateAsync(id);
        toast.success("Demande prise en charge");
      } catch {
        toast.error("Echec du traitement");
      }
    },
    [processMutation],
  );

  const handleComplete = useCallback(
    async (id: string) => {
      if (!confirm("Marquer cette demande comme terminee ?")) return;
      try {
        await completeMutation.mutateAsync(id);
        toast.success("Demande terminee");
      } catch {
        toast.error("Echec de la completion");
      }
    },
    [completeMutation],
  );

  const handleReject = useCallback(
    async (id: string) => {
      if (!confirm("Rejeter cette demande RGPD ?")) return;
      try {
        await rejectMutation.mutateAsync(id);
        toast.success("Demande rejetee");
      } catch {
        toast.error("Echec du rejet");
      }
    },
    [rejectMutation],
  );

  const columns: readonly ColumnDef<GDPRRequest>[] = [
    {
      id: "id",
      header: "ID",
      cell: (row) => (
        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-xs text-stone-600">
          {row.id.slice(0, 8)}
        </code>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => {
        const Icon = TYPE_ICONS[row.type];
        return (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${TYPE_COLORS[row.type]}`}
          >
            <Icon className="h-3 w-3" />
            {TYPE_LABELS[row.type]}
          </span>
        );
      },
    },
    {
      id: "subject",
      header: "Demandeur",
      cell: (row) => (
        <div>
          <p className="text-sm font-medium text-stone-900">{row.subject_name}</p>
          <p className="mt-0.5 text-xs text-stone-500">{row.subject_email}</p>
        </div>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      id: "submitted_at",
      header: "Soumis le",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDate(row.submitted_at)}</span>
      ),
    },
    {
      id: "sla",
      header: "Delai SLA",
      cell: (row) => <SLABadge request={row} />,
    },
    {
      id: "handled_by",
      header: "Assigne a",
      cell: (row) => (
        <span className="text-sm text-stone-500">{row.handled_by ?? "\u2014"}</span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          {row.status === "pending" && (
            <button
              onClick={() => handleProcess(row.id)}
              className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
              title="Traiter"
            >
              <Play className="h-3 w-3" />
              Traiter
            </button>
          )}
          {row.status === "processing" && (
            <>
              <button
                onClick={() => handleComplete(row.id)}
                className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
                title="Terminer"
              >
                <CheckCircle className="h-3 w-3" />
                Terminer
              </button>
              <button
                onClick={() => handleReject(row.id)}
                className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
                title="Rejeter"
              >
                <XCircle className="h-3 w-3" />
                Rejeter
              </button>
            </>
          )}
          {(row.status === "completed" || row.status === "rejected") && (
            <span className="text-xs text-stone-400">
              {formatDate(row.completed_at)}
            </span>
          )}
        </div>
      ),
    },
  ];

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-10 w-10 text-red-400" />
          <p className="mt-3 text-sm font-medium text-stone-700">
            Erreur lors du chargement des demandes RGPD
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Veuillez reessayer ou contacter le support.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Conformite RGPD
              </h1>
              <p className="text-sm text-stone-500">
                Gestion des demandes de droits des personnes (DSAR)
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouvelle demande
          </Button>
        </div>

        {/* Legal banner */}
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <Shield className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Conformite RGPD</p>
            <p className="mt-0.5 text-xs text-blue-700">
              Delai legal de traitement : 30 jours. Flux de statut : Recue &rarr; En cours &rarr;
              Terminee / Rejetee
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total demandes"
            value={pagination?.total ?? totalRequests}
            icon={<Shield className="h-4 w-4" />}
          />
          <KPICard
            label="En cours"
            value={processingCount}
            icon={<Play className="h-4 w-4" />}
          />
          <KPICard
            label="Terminees"
            value={completedCount}
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="En retard"
            value={overdueCount}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
        </div>

        {/* Overdue alert */}
        {overdueCount > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">
              <span className="font-semibold">{overdueCount} demande{overdueCount > 1 ? "s" : ""} en retard</span> \u2014
              Le delai legal de 30 jours est depasse. Traitez ces demandes en priorite.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar filters={FILTER_CONFIGS} values={filters} onChange={handleFilterChange} />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={requests}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucune demande RGPD trouvée."
        />
      </div>

      <CreateGDPRModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}
